import { ScrollArea } from "@/components/ui/scroll-area";
import { Transcript } from "@/types/prompt";
import { ChatConversation } from "@/components/ui/chat-bubble";
import { useState, useEffect } from "react";

interface ChatLikeTranscriptProps {
  transcript: Transcript;
  maxHeight?: string;
}

interface Message {
  role: string;
  message: string;
  timestamp?: string;
}

export default function ChatLikeTranscript({
  transcript,
  maxHeight = "500px",
}: ChatLikeTranscriptProps) {
  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (transcript) {
      parseTranscript(transcript.content);
    }
  }, [transcript]);

  const parseTranscript = (content: string) => {
    // Try to detect common transcript formats
    const lines = content.split("\n").filter((line) => line.trim() !== "");
    const parsedMessages: Message[] = [];

    // Check if the transcript has a clear format with speaker indicators
    const hasUserPrefix = lines.some(
      (line) =>
        line.startsWith("User:") ||
        line.startsWith("Customer:") ||
        line.startsWith("Client:"),
    );

    const hasAssistantPrefix = lines.some(
      (line) =>
        line.startsWith("Assistant:") ||
        line.startsWith("Agent:") ||
        line.startsWith("AI:") ||
        line.startsWith("Bot:"),
    );

    if (hasUserPrefix || hasAssistantPrefix) {
      // Process structured format
      let currentRole: "user" | "assistant" | "system" = "system";
      let currentContent = "";

      for (const line of lines) {
        if (
          line.startsWith("User:") ||
          line.startsWith("Customer:") ||
          line.startsWith("Client:")
        ) {
          // Save previous message if exists
          if (currentContent) {
            parsedMessages.push({
              role: currentRole,
              message: currentContent.trim(),
            });
          }
          currentRole = "user";
          currentContent = line.replace(/^(User:|Customer:|Client:)\s*/, "");
        } else if (
          line.startsWith("Assistant:") ||
          line.startsWith("Agent:") ||
          line.startsWith("AI:") ||
          line.startsWith("Bot:")
        ) {
          // Save previous message if exists
          if (currentContent) {
            parsedMessages.push({
              role: currentRole,
              message: currentContent.trim(),
            });
          }
          currentRole = "assistant";
          currentContent = line.replace(/^(Assistant:|Agent:|AI:|Bot:)\s*/, "");
        } else {
          // Continue current message
          currentContent += "\n" + line;
        }
      }

      // Add the last message
      if (currentContent) {
        parsedMessages.push({
          role: currentRole,
          message: currentContent.trim(),
        });
      }
    } else {
      // Try to detect alternating pattern (assuming first is user, second is assistant, etc.)
      let isUser = true;
      for (const line of lines) {
        if (line.trim()) {
          parsedMessages.push({
            role: isUser ? "user" : "assistant",
            message: line.trim(),
          });
          isUser = !isUser;
        }
      }
    }

    // If we couldn't parse it into a conversation, just show it as a system message
    if (parsedMessages.length === 0) {
      parsedMessages.push({
        role: "system",
        message: content,
      });
    }

    setMessages(parsedMessages);
  };

  return (
    <ScrollArea
      className={`border rounded-md p-4 ${maxHeight ? `h-[${maxHeight}]` : ""}`}
    >
      <ChatConversation messages={messages} />
    </ScrollArea>
  );
}
